import type { EnsembleModel } from '../Models/EnsembleModel';
import type { StudentContactModel, StudentModel } from '../Models/StudentModel';
import type { Language } from '../Models/enums';

/**
 * The name to put on screen, which is not always the name in the database.
 *
 * A student's `name` is their identity: every lesson, slot, contact and
 * receipt joins on it, and it never changes because the reader switched
 * language. What can change is how it is spelled on screen. A tutor working
 * in Hebrew stores "יוסי כהן" and, with the interface in English, would
 * otherwise read a Hebrew name in a Latin-script layout.
 *
 * So the rule is one line long and applies everywhere: in English, use the
 * English spelling if there is one; otherwise use the canonical one. Never the
 * other way round, and never a blank.
 *
 * Anything that identifies rather than displays - a URL, a lookup key, a
 * request body, a WhatsApp message the student's own family reads - keeps
 * using `name`. These helpers are for what the tutor looks at.
 */

function pick(preferred: string | null | undefined, canonical: string): string {
  const text = preferred?.trim();
  return text ? text : canonical;
}

/** The student's name as this reader should see it. */
export function studentDisplayName(
  student: Pick<StudentModel, 'name' | 'firstNameEn' | 'lastNameEn'>,
  language: Language
): string {
  if (language !== 'en') return student.name;
  const composed = [student.firstNameEn, student.lastNameEn]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(' ');
  return pick(composed, student.name);
}

/** The student's address as this reader should see it, or null if there is none. */
export function studentDisplayAddress(
  student: Pick<StudentModel, 'address' | 'addressEn'>,
  language: Language
): string | null {
  if (language !== 'en') return student.address;
  const preferred = student.addressEn?.trim();
  if (preferred) return preferred;
  return student.address;
}

/** The student's notes as this reader should see them, or null if there are none. */
export function studentDisplayNotes(
  student: Pick<StudentModel, 'notes' | 'notesEn'>,
  language: Language
): string | null {
  if (language !== 'en') return student.notes;
  return student.notesEn?.trim() || student.notes;
}

/** A contact's name as this reader should see it. */
export function contactDisplayName(
  contact: Pick<StudentContactModel, 'name' | 'nameEn'>,
  language: Language
): string {
  return language === 'en' ? pick(contact.nameEn, contact.name) : contact.name;
}

/** Who the contact is to the student, as this reader should see it. */
export function contactDisplayRelation(
  contact: Pick<StudentContactModel, 'relation' | 'relationEn'>,
  language: Language
): string | null {
  if (language !== 'en') return contact.relation;
  return contact.relationEn?.trim() || contact.relation;
}

/** The group's name as this reader should see it. */
export function ensembleDisplayName(
  ensemble: Pick<EnsembleModel, 'name' | 'nameEn'>,
  language: Language
): string {
  return language === 'en' ? pick(ensemble.nameEn, ensemble.name) : ensemble.name;
}

/**
 * The display name for a bare student name, looked up in the roster.
 *
 * Lessons, receipts and reminders carry a student's identity rather than the
 * student, so a screen showing one has only the name to go on. Falls back to
 * the name itself, which is what shows for somebody since deleted or renamed.
 */
export function displayNameOf(
  name: string,
  students: readonly StudentModel[],
  language: Language
): string {
  if (language !== 'en') return name;
  const student = students.find(candidate => candidate.name === name);
  return student ? studentDisplayName(student, language) : name;
}

/**
 * The display name for whoever a document is made out to.
 *
 * A receipt can be addressed to the student, to an organization or to one of
 * the student's contacts, and carries only the name. Students are tried first
 * because a name there is unique; a contact's is not, so the first match wins.
 */
export function partyDisplayName(
  name: string,
  students: readonly StudentModel[],
  language: Language
): string {
  if (language !== 'en') return name;
  const student = students.find(candidate => candidate.name === name);
  if (student) return studentDisplayName(student, language);
  for (const candidate of students) {
    const contact = candidate.contacts?.find(item => item.name === name);
    if (contact) return contactDisplayName(contact, language);
  }
  return name;
}

/**
 * Everything a student can be found by when searching, lowercased.
 *
 * Both spellings, always, whatever the interface language: a tutor who typed
 * an English name once should be able to search for it in Hebrew and the
 * other way round, without first switching the interface.
 */
export function searchableText(student: StudentModel): string {
  return [
    student.name,
    student.firstNameEn,
    student.lastNameEn,
    student.address,
    student.addressEn,
    student.phone
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}
